/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.76: ParentingScheduleDialog and ScheduleProposalsBanner seed
 * local form state inside `useEffect` after fetching/deriving the
 * per-child schedule. The experimental React 19 rule mis-flags this
 * pattern; the seeding is intentional and audited.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { downloadFile } from "@/lib/downloadFile";
import { generateIdempotencyKey, idempotencyHeader } from "@/lib/idempotency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChildDots } from "@/components/ChildDots";
import { Repeat, Check, X as XIcon, Inbox, Pencil, CalendarPlus } from "lucide-react";
import { getParentColorById, tintBg } from "@/lib/parentColor";
import { toast } from "sonner";

/**
 * Calendar — schedule controls (banners, proposals dialog, recurrence,
 * reminder fields). Extracted from pages/CalendarPage.jsx in the
 * v77.99.19 Phase 3 frontend decomposition pass (slice 3a — Calendar).
 *
 * Tail-only extraction: all the helpers below already existed as
 * self-contained functions at the bottom of CalendarPage.jsx. This
 * relocation moves ~1,050 lines of presentational + state-local logic
 * out of the page file with zero behaviour changes.
 *
 * Public API (named exports used by CalendarPage):
 *  - ScheduleProposalsBanner
 *  - ScheduleAuditBanner
 *  - ParentingScheduleDialog
 *  - RecurrenceFields
 *  - ReminderFields
 *
 * Module-private constants moved with the tail:
 *  - FREQ_OPTIONS, WEEKDAYS (used by RecurrenceFields)
 *  - WEEKDAY_OPTIONS, WEEKDAY_SHORT (used by ParentingScheduleDialog)
 *  - PATTERN_LABEL, REMINDER_PRESETS, TWO_TWO_THREE_CYCLE
 *  - _patternLabel, _parentName, detectTransitions
 */
// eslint-disable-next-line no-unused-vars -- Module marker for the v77.99.19 Phase 3 decomposition
const _PHASE3_NOTE = "v77.99.19 Phase 3 frontend decomposition";

const FREQ_OPTIONS = [
  { value: "none", label: "Does not repeat" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Bi-weekly (fortnightly)" },
  { value: "monthly", label: "Monthly" },
];
const WEEKDAYS = [
  { v: 0, l: "Mon" }, { v: 1, l: "Tue" }, { v: 2, l: "Wed" }, { v: 3, l: "Thu" },
  { v: 4, l: "Fri" }, { v: 5, l: "Sat" }, { v: 6, l: "Sun" },
];


// ---- Schedule change proposals banner ------------------------------------
// v77.26 — Renders one row per pending schedule change proposal. Each row
// shows the diff (current → proposed) and the action buttons appropriate
// to the viewer:
//   * primary parent → Approve / Reject
//   * the proposer themselves → Cancel
//   * anyone else with access to the child → read-only "pending" pill
const PATTERN_LABEL = {
  alternating_weeks: "Weekly split — alternating weeks",
  two_two_three: "2-2-3 rotation",
  every_second_weekend: "Every second weekend",
  custom_weekly: "Custom weekly",
};

function _patternLabel(p) {
  return PATTERN_LABEL[p] || p || "—";
}

function _parentName(membersById, pid) {
  return membersById.get(pid)?.name || "—";
}

function ScheduleProposalDiffRow({ proposal, membersById, childrenById }) {
  // Compact two-column diff. Hidden when current === proposed (e.g. a
  // co-parent proposed minor changes only to handover times — we still
  // show the row so primary can see it but mark unchanged columns).
  const cur = proposal.current || {};
  const prop = proposal.proposed || {};
  const fields = [
    { k: "pattern", label: "Pattern", render: (v) => _patternLabel(v) },
    { k: "start_date", label: "Start", render: (v) => v || "—" },
    { k: "parent_a_id", label: "Parent A", render: (v) => _parentName(membersById, v) },
    { k: "parent_b_id", label: "Parent B", render: (v) => _parentName(membersById, v) },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 text-[11.5px]">
      {fields.map((f) => {
        const before = f.render(cur[f.k]);
        const after = f.render(prop[f.k]);
        const changed = before !== after;
        return (
          <div key={f.k} className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-[0.08em] text-[#6F828A] w-16 flex-shrink-0">{f.label}</span>
            <span className="text-[#6F828A] line-through">{before}</span>
            <span className={`${changed ? "text-[#116B6E] font-medium" : "text-[#6F828A]"}`}>→ {after}</span>
          </div>
        );
      })}
    </div>
  );
}

export function ScheduleProposalsBanner({ proposals, isPrimaryParent, currentUserId, members, childList = [], onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [rejectMessage, setRejectMessage] = useState("");
  const membersById = useMemo(() => {
    const m = new Map();
    (members || []).forEach((u) => m.set(u.id, u));
    return m;
  }, [members]);
  const childrenById = useMemo(() => {
    const m = new Map();
    (childList || []).forEach((c) => m.set(c.id, c));
    return m;
  }, [childList]);

  const approve = async (pid) => {
    setBusyId(pid);
    try {
      await api.post(`/custody/schedule/proposals/${pid}/approve`);
      toast.success("Schedule change approved");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't approve");
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = async () => {
    if (!rejecting) return;
    setBusyId(rejecting);
    try {
      await api.post(`/custody/schedule/proposals/${rejecting}/reject`, { message: rejectMessage.trim() || undefined });
      toast.success("Schedule change declined");
      setRejecting(null);
      setRejectMessage("");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reject");
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (pid) => {
    if (!window.confirm("Cancel this schedule change request?")) return;
    setBusyId(pid);
    try {
      await api.post(`/custody/schedule/proposals/${pid}/cancel`);
      toast.success("Request cancelled");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't cancel");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="rounded-md border border-[#A7D8DA] bg-[#E0F2F2] p-3" data-testid="schedule-proposals-banner">
      <div className="flex items-center gap-2 mb-2">
        <Inbox className="w-4 h-4 text-[#116B6E]" strokeWidth={1.6} />
        <span className="text-[12px] font-medium text-[#0F5658]">
          {proposals.length === 1
            ? "Pending schedule change request"
            : `${proposals.length} pending schedule change requests`}
        </span>
      </div>
      <div className="space-y-3">
        {proposals.map((p) => {
          const child = childrenById.get(p.child_id);
          const isOwnProposal = p.proposed_by_id === currentUserId;
          return (
            <div
              key={p.id}
              className="rounded-md border border-[#A7D8DA] bg-white p-3"
              data-testid={`schedule-proposal-row-${p.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[12.5px] font-medium text-[#2E3A3F] inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: child?.color || "#1F9FA4" }} />
                    {p.child_name || child?.name || "Child"}
                  </div>
                  <div className="text-[11px] text-[#6F828A] mt-0.5">
                    {isOwnProposal ? "You requested" : `${p.proposed_by_name || "Co-parent"} requested`} on {(p.created_at || "").slice(0, 10)}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {isPrimaryParent && !isOwnProposal && (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => approve(p.id)}
                        disabled={busyId === p.id}
                        className="bg-[#116B6E] hover:bg-[#0F5658] text-white h-7 text-[11.5px]"
                        data-testid={`schedule-proposal-approve-${p.id}`}
                      >
                        <Check className="w-3.5 h-3.5 mr-1" strokeWidth={2} />
                        Approve
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => { setRejecting(p.id); setRejectMessage(""); }}
                        disabled={busyId === p.id}
                        className="border-[#D9E1E3] text-[#8C2A22] hover:bg-[#FBEFED] h-7 text-[11.5px]"
                        data-testid={`schedule-proposal-reject-${p.id}`}
                      >
                        <XIcon className="w-3.5 h-3.5 mr-1" strokeWidth={2} />
                        Decline
                      </Button>
                    </>
                  )}
                  {isOwnProposal && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => cancel(p.id)}
                      disabled={busyId === p.id}
                      className="text-[#6F828A] hover:bg-[#EEF3F4] h-7 text-[11.5px]"
                      data-testid={`schedule-proposal-cancel-${p.id}`}
                    >
                      Cancel request
                    </Button>
                  )}
                  {!isPrimaryParent && !isOwnProposal && (
                    <span className="text-[11px] text-[#6F828A] inline-flex items-center px-2 py-0.5 border border-[#D9E1E3] rounded-sm bg-white">
                      Awaiting primary review
                    </span>
                  )}
                </div>
              </div>
              <ScheduleProposalDiffRow proposal={p} membersById={membersById} childrenById={childrenById} />
              {p.message && (
                <div className="mt-2 text-[11.5px] text-[#2E3A3F] italic border-l-2 border-[#A7D8DA] pl-2">
                  &ldquo;{p.message}&rdquo;
                </div>
              )}
              {rejecting === p.id && (
                <div className="mt-3 border-t border-[#EEF3F4] pt-3 space-y-2" data-testid={`schedule-proposal-reject-form-${p.id}`}>
                  <Label className="text-[10.5px] uppercase tracking-[0.08em] text-[#6F828A]">
                    Reason for declining <span className="text-[#9AAAB1] normal-case tracking-normal">(optional, sent to {p.proposed_by_name || "the co-parent"})</span>
                  </Label>
                  <Textarea
                    value={rejectMessage}
                    onChange={(e) => setRejectMessage(e.target.value)}
                    rows={2}
                    maxLength={1000}
                    placeholder="Add context — keeps things civil and clear."
                    className="text-sm"
                    data-testid={`schedule-proposal-reject-message-${p.id}`}
                  />
                  <div className="flex justify-end gap-2">
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setRejecting(null); setRejectMessage(""); }} disabled={busyId === p.id}>
                      Back
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={submitReject}
                      disabled={busyId === p.id}
                      className="bg-[#8C2A22] hover:bg-[#6F2018] text-white h-7 text-[11.5px]"
                      data-testid={`schedule-proposal-reject-submit-${p.id}`}
                    >
                      Decline request
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ---- Parenting schedule setup dialog -------------------------------------
// Defines the repeating custody pattern. Members are pulled from the household
// (so the dropdowns are pre-scoped), and the user picks one of 4 patterns.
// The custom_weekly pattern lets the operator assign each weekday to either
// parent A or parent B for a fully-custom weekly cycle.
const WEEKDAY_OPTIONS = [
  { v: 0, l: "Monday" },
  { v: 1, l: "Tuesday" },
  { v: 2, l: "Wednesday" },
  { v: 3, l: "Thursday" },
  { v: 4, l: "Friday" },
  { v: 5, l: "Saturday" },
  { v: 6, l: "Sunday" },
];

// v71 — auto-detect transition points for a given pattern + custom layout.

// ---- Schedule data audit banner -----------------------------------------
// v77.31 — Surfaces stale schedules where parent_a_id / parent_b_id no
// longer matches the child's active ChildParentAccess (data drift from
// before v77.25's modal eligibility fix), plus pre-v77.22 legacy
// household-wide schedule rows (`child_id=None`).
//
// Read access for everyone with calendar visibility; the "Reset"
// action is gated to the primary parent because only they can
// DELETE /api/custody/schedule (the same primary-only gate as PUT
// since v77.26).
export function ScheduleAuditBanner({ audit, isPrimaryParent, onChanged }) {
  const [busyCid, setBusyCid] = useState(null);
  const stale = audit?.stale || [];
  const legacy = audit?.legacy_household_wide || [];
  const total = stale.length + legacy.length;
  if (total === 0) return null;

  const resetSchedule = async (childId) => {
    if (!window.confirm("Clear this schedule? You'll need to set a new one with the correct co-parent.")) return;
    setBusyCid(childId);
    try {
      await api.delete(`/custody/schedule?child_id=${encodeURIComponent(childId)}`);
      toast.success("Schedule cleared");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reset");
    } finally {
      setBusyCid(null);
    }
  };

  const resetLegacyHouseholdWide = async () => {
    if (!window.confirm("Remove the legacy household-wide schedule? Each child should have their own schedule.")) return;
    setBusyCid("__legacy__");
    try {
      // The DELETE endpoint accepts a missing child_id and treats it
      // as the legacy household-wide row.
      await api.delete("/custody/schedule");
      toast.success("Legacy schedule cleared");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reset");
    } finally {
      setBusyCid(null);
    }
  };

  return (
    <div
      className="rounded-md border border-[#E6CFA2] bg-[#FDF4E2] p-3"
      data-testid="schedule-audit-banner"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="w-4 h-4 inline-flex items-center justify-center text-[#6F4E1E] flex-shrink-0">
          {/* triangle-alert glyph from lucide-react via the existing import set */}
          <Pencil className="w-3.5 h-3.5" strokeWidth={1.8} />
        </span>
        <span className="text-[12px] font-medium text-[#5C3A1E]">
          Schedule needs review
          {total > 1 ? ` · ${total} entries` : ""}
        </span>
      </div>
      <div className="space-y-2">
        {stale.map((row) => (
          <div
            key={row.child_id}
            className="rounded-md border border-[#E6CFA2] bg-white p-3"
            data-testid={`schedule-audit-row-${row.child_id}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-[12.5px] font-medium text-[#2E3A3F] inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: row.child_color || "#1F9FA4" }} />
                  {row.child_name || "Child"}
                </div>
                <div className="text-[11px] text-[#5C3A1E] mt-1 leading-relaxed">
                  {(row.invalid_parent_names || []).map((n, i) => (
                    <span key={`inv-${n}-${i}`}><b>{n}</b>{i < row.invalid_parent_names.length - 1 ? ", " : ""}</span>
                  ))}
                  {(row.invalid_parent_names || []).length === 1 ? " is" : " are"} no longer linked to this child.
                  The schedule won&apos;t apply until you set a new one.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {isPrimaryParent ? (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => resetSchedule(row.child_id)}
                    disabled={busyCid === row.child_id}
                    className="bg-[#8C5A1E] hover:bg-[#6F4515] text-white h-7 text-[11.5px]"
                    data-testid={`schedule-audit-reset-${row.child_id}`}
                  >
                    Reset schedule
                  </Button>
                ) : (
                  <span className="text-[11px] text-[#6F828A] inline-flex items-center px-2 py-0.5 border border-[#D9E1E3] rounded-sm bg-white">
                    Awaiting primary parent
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
        {legacy.length > 0 && (
          <div
            className="rounded-md border border-[#E6CFA2] bg-white p-3"
            data-testid="schedule-audit-legacy-row"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-[12.5px] font-medium text-[#2E3A3F]">
                  Legacy household-wide schedule
                </div>
                <div className="text-[11px] text-[#5C3A1E] mt-1 leading-relaxed">
                  This was a single schedule applied to every child before v77.22.
                  Each child should have their own schedule now — clear this one
                  to silence the warning.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {isPrimaryParent ? (
                  <Button
                    type="button"
                    size="sm"
                    onClick={resetLegacyHouseholdWide}
                    disabled={busyCid === "__legacy__"}
                    className="bg-[#8C5A1E] hover:bg-[#6F4515] text-white h-7 text-[11.5px]"
                    data-testid="schedule-audit-reset-legacy"
                  >
                    Clear legacy schedule
                  </Button>
                ) : (
                  <span className="text-[11px] text-[#6F828A] inline-flex items-center px-2 py-0.5 border border-[#D9E1E3] rounded-sm bg-white">
                    Awaiting primary parent
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// Mirror of `_default_handover_times` in server.py so the editor can show
// the natural list without an extra round-trip. Returns
//   [{ day: 0..6, from: 'A'|'B', to: 'A'|'B' }, ...]
// in cycle order. Time defaults are filled in at render time from the
// handoverTimeMap state so the user's edits persist across pattern flips.
const TWO_TWO_THREE_CYCLE = ["A", "A", "B", "B", "A", "A", "A", "B", "B", "A", "A", "B", "B", "B"];

function detectTransitions(pattern, startDateStr, customWeekly, weekendStartDay, weekendEndDay) {
  const startDate = startDateStr ? new Date(`${startDateStr}T00:00:00`) : new Date();
  // JS getDay(): 0=Sun..6=Sat. Convert to Mon=0..Sun=6.
  const startWd = (startDate.getDay() + 6) % 7;
  const out = [];
  if (pattern === "alternating_weeks") {
    // Two transitions per 14-day cycle, both anchored to the same weekday.
    out.push({ day: startWd, from: "A", to: "B" });
    out.push({ day: startWd, from: "B", to: "A" });
    return out;
  }
  if (pattern === "two_two_three") {
    for (let i = 0; i < 14; i++) {
      const prev = TWO_TWO_THREE_CYCLE[(i - 1 + 14) % 14];
      const cur = TWO_TWO_THREE_CYCLE[i];
      if (prev === cur) continue;
      const wd = (startWd + i) % 7;
      out.push({ day: wd, from: prev, to: cur });
    }
    return out;
  }
  if (pattern === "custom_weekly") {
    const cw = (customWeekly || "AABBAAB").toUpperCase();
    if (cw.length !== 7) return out;
    for (let i = 0; i < 7; i++) {
      const prev = cw[(i - 1 + 7) % 7];
      const cur = cw[i];
      if (prev === cur) continue;
      out.push({ day: i, from: prev, to: cur });
    }
    return out;
  }
  if (pattern === "every_second_weekend") {
    out.push({ day: weekendStartDay, from: "A", to: "B" });
    out.push({ day: weekendEndDay, from: "B", to: "A" });
    return out;
  }
  return out;
}

const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function ParentingScheduleDialog({ open, onClose, members, currentUserId, isPrimaryParent = false, existing, onSaved, childList = [], byChild = {} }) {
  // v77.22.8 — Schedules are STRICTLY per-child. There is no "All
  // children (household default)" option anymore — it produced
  // misleading parent options (the full household roster) and
  // conflicted with per-child schedules. The dialog requires a child
  // selection before saving, and the parent selectors are scoped to
  // ONLY the logged-in user + active co-parents linked to that child.
  //
  // Archived children are excluded at the data source (v77.22.6 fetch
  // already filters include_archived=false).
  const [scopeChildId, setScopeChildId] = useState(existing?.child_id || (childList[0]?.id || ""));
  const activeExisting = scopeChildId ? (byChild?.[scopeChildId]?.schedule || null) : null;
  const [pattern, setPattern] = useState(activeExisting?.pattern || "alternating_weeks");
  const [startDate, setStartDate] = useState(activeExisting?.start_date || new Date().toISOString().slice(0, 10));
  const [parentAId, setParentAId] = useState(activeExisting?.parent_a_id || currentUserId || "");
  const [parentBId, setParentBId] = useState(activeExisting?.parent_b_id || "");
  const [customWeekly, setCustomWeekly] = useState(activeExisting?.custom_weekly || "AABBAAB");
  // Weekend-window settings for the every_second_weekend pattern. Defaults
  // mirror the backend defaults (Fri 17:00 → Sun 19:00).
  const [weekendStartDay, setWeekendStartDay] = useState(
    typeof activeExisting?.weekend_start_day === "number" ? activeExisting.weekend_start_day : 4,
  );
  const [weekendStartTime, setWeekendStartTime] = useState(activeExisting?.weekend_start_time || "17:00");
  const [weekendEndDay, setWeekendEndDay] = useState(
    typeof activeExisting?.weekend_end_day === "number" ? activeExisting.weekend_end_day : 6,
  );
  const [weekendEndTime, setWeekendEndTime] = useState(activeExisting?.weekend_end_time || "19:00");
  // v71 — per-transition handover times for every pattern. Auto-derived
  // from the chosen pattern, but each entry's time is editable. Storage
  // is `{ "key": "HH:MM" }` keyed by `${day}-${from}-${to}` so flipping
  // a custom-weekly day or changing patterns doesn't blow away values
  // for transitions that still apply.
  const [handoverTimeMap, setHandoverTimeMap] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    // v77.22.8 — Auto-pick the first active child if none is scoped
    // yet (e.g. dialog opens for a household where only a legacy
    // household-wide schedule existed, which had child_id=null).
    if (!scopeChildId && childList.length > 0) {
      setScopeChildId(childList[0].id);
      return; // next effect run will seed the form
    }
    // Always seed from the per-child schedule. If none is set for the
    // current scope child, leave the form with sensible defaults.
    const src = scopeChildId ? (byChild?.[scopeChildId]?.schedule || null) : null;
    setPattern(src?.pattern || "alternating_weeks");
    setStartDate(src?.start_date || new Date().toISOString().slice(0, 10));
    setParentAId(src?.parent_a_id || currentUserId || "");
    setParentBId(src?.parent_b_id || "");
    setCustomWeekly(src?.custom_weekly || "AABBAAB");
    setWeekendStartDay(typeof src?.weekend_start_day === "number" ? src.weekend_start_day : 4);
    setWeekendStartTime(src?.weekend_start_time || "17:00");
    setWeekendEndDay(typeof src?.weekend_end_day === "number" ? src.weekend_end_day : 6);
    setWeekendEndTime(src?.weekend_end_time || "19:00");
    const seed = {};
    (src?.handover_times || []).forEach((h) => {
      seed[`${h.day}-${h.from_parent}-${h.to_parent}`] = h.time;
    });
    setHandoverTimeMap(seed);
  }, [open, currentUserId, scopeChildId, byChild, childList]);

  // v77.22.8 — Eligible parents for the currently-selected child.
  // v77.25 — FIX: was previously reading from `child.sharing.coparents`
  // which excludes the PRIMARY parent (it only lists granted
  // co-parents). When a co-parent was logged in, the dropdown for
  // "Parent B" was empty because the primary parent was missing from
  // the eligible set. The canonical source is `child.parent_ids` —
  // populated server-side as `primary + all active memberships` — so
  // we use that instead. Falls back to the legacy logic when
  // `parent_ids` isn't present (older child docs from before v77.10).
  //
  // v77.99.43 — Always merge in any household-level placeholder
  // co-parent. The placeholder is a household-wide stand-in for a
  // not-yet-joined parent and is NOT stored on `child.parent_ids`
  // (which only contains real registered users). Previously this
  // meant single-parent / parallel-parenting users couldn't pick
  // the placeholder as Parent B at all — schedule setup blocked
  // entirely. Including it here unblocks the whole flow without
  // touching the schedule data model.
  const eligibleParents = useMemo(() => {
    if (!scopeChildId) return [];
    const child = childList.find((c) => c.id === scopeChildId);
    if (!child) return [];
    const allowed = new Set();
    if (Array.isArray(child.parent_ids) && child.parent_ids.length > 0) {
      child.parent_ids.forEach((pid) => allowed.add(pid));
    } else {
      // Legacy fallback — pre-v77.10 child docs without `parent_ids`.
      (child?.sharing?.coparents || []).forEach((cp) => allowed.add(cp.user_id));
    }
    // Always include the logged-in user so they appear as "you" even
    // if their membership row hasn't been re-indexed yet (e.g. the
    // tail end of an invite-acceptance flow).
    if (currentUserId) allowed.add(currentUserId);
    // v77.99.43 — Include the household placeholder co-parent so a
    // not-yet-signed-up Parent B can still anchor the schedule.
    (members || []).forEach((m) => { if (m.is_placeholder) allowed.add(m.id); });
    return members.filter((m) => allowed.has(m.id));
  }, [scopeChildId, childList, members, currentUserId]);
  const otherMembers = eligibleParents.filter((m) => m.id !== parentAId);
  const PATTERNS = [
    { v: "alternating_weeks", l: "Weekly split — alternating weeks" },
    { v: "two_two_three", l: "2-2-3 schedule (14-day cycle)" },
    { v: "every_second_weekend", l: "Every second weekend" },
    { v: "custom_weekly", l: "Fully custom weekly pattern" },
  ];

  // Pretty preview of the configured weekend window.
  const weekendDuration = ((weekendEndDay - weekendStartDay) % 7 + 7) % 7 + 1;
  const weekendPreview = `${WEEKDAY_OPTIONS[weekendStartDay]?.l || "Friday"} ${weekendStartTime} → ${WEEKDAY_OPTIONS[weekendEndDay]?.l || "Sunday"} ${weekendEndTime} (${weekendDuration} day${weekendDuration === 1 ? "" : "s"})`;

  // v71 — auto-derived list of handover transitions for the chosen pattern.
  // Recomputes whenever pattern / customWeekly / start date / weekend
  // anchors change so the UI always reflects the live cycle.
  const transitions = detectTransitions(
    pattern, startDate, customWeekly, weekendStartDay, weekendEndDay,
  );
  const transitionKey = (t) => `${t.day}-${t.from}-${t.to}`;
  const transitionTime = (t) => handoverTimeMap[transitionKey(t)] || "17:00";
  const setTransitionTime = (t, value) => {
    setHandoverTimeMap((prev) => ({ ...prev, [transitionKey(t)]: value }));
  };
  const parentLabel = (letter) => {
    if (letter === "A") return members.find((m) => m.id === parentAId)?.name || "Parent A";
    return members.find((m) => m.id === parentBId)?.name || "Parent B";
  };

  // Build the payload's handover_times — only for the transitions
  // currently relevant to the chosen pattern. This avoids stale entries
  // from a previously-selected pattern leaking into the saved record.
  const buildHandoverPayload = () => transitions.map((t) => ({
    day: t.day,
    time: transitionTime(t),
    from_parent: t.from,
    to_parent: t.to,
  }));

  // v71.2 — unified "Handover preview" string. Each pattern surfaces its
  // own friendly summary so the user sees their settings produce a
  // sensible week before they save. Tone matches the rest of the
  // editor: neutral, child-focused, no Parent A/B labels, real names.
  const buildSchedulePreview = () => {
    const aName = parentAId ? (members.find((m) => m.id === parentAId)?.name || "Parent A") : "Parent A";
    const bName = parentBId ? (members.find((m) => m.id === parentBId)?.name || "Parent B") : "Parent B";
    if (pattern === "alternating_weeks") {
      return `7 days with ${aName} · 7 days with ${bName} · 1 handover each week`;
    }
    if (pattern === "two_two_three") {
      return `7 days with ${aName} · 7 days with ${bName} over 2 weeks · 6 handovers per fortnight`;
    }
    if (pattern === "custom_weekly") {
      const cw = (customWeekly || "").toUpperCase();
      const aDays = (cw.match(/A/g) || []).length;
      const bDays = (cw.match(/B/g) || []).length;
      const handoverCount = transitions.length;
      const handoverWord = handoverCount === 1 ? "handover" : "handovers";
      return `${aDays} day${aDays === 1 ? "" : "s"} with ${aName} · ${bDays} day${bDays === 1 ? "" : "s"} with ${bName} · ${handoverCount} ${handoverWord} per week`;
    }
    return "";
  };
  const schedulePreview = buildSchedulePreview();

  // v77.26 — Co-parents submit a CHANGE REQUEST instead of editing
  // directly. The primary parent reviews + approves/rejects from the
  // calendar header banner. Optional message lets the co-parent
  // explain context to the primary.
  const [proposalMessage, setProposalMessage] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    // v77.22.8 — Enforce strict per-child rule and eligible-parent rule
    // at the UI layer. Backend also validates, but failing fast here
    // gives the user a clearer error.
    if (!scopeChildId) { toast.error("Pick a child first"); return; }
    if (!parentAId || !parentBId) { toast.error("Select both parents"); return; }
    if (parentAId === parentBId) { toast.error("Parent A and B must differ"); return; }
    const eligibleIds = new Set(eligibleParents.map((m) => m.id));
    if (!eligibleIds.has(parentAId) || !eligibleIds.has(parentBId)) {
      toast.error("Both parents must be linked to this child");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        child_id: scopeChildId,
        pattern, start_date: startDate, parent_a_id: parentAId, parent_b_id: parentBId,
        custom_weekly: pattern === "custom_weekly" ? customWeekly : null,
        weekend_start_day: weekendStartDay,
        weekend_start_time: weekendStartTime,
        weekend_end_day: weekendEndDay,
        weekend_end_time: weekendEndTime,
        handover_times: buildHandoverPayload(),
      };
      if (isPrimaryParent) {
        // Primary parent saves directly via PUT.
        await api.put("/custody/schedule", payload);
        toast.success("Parenting schedule saved");
      } else {
        // Co-parent submits a proposal — primary will approve/reject.
        await api.post("/custody/schedule/propose", {
          ...payload,
          message: (proposalMessage || "").trim() || undefined,
        });
        toast.success("Schedule change requested. The primary parent will review.");
      }
      onSaved?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "object" && detail?.message
          ? detail.message
          : typeof detail === "string"
            ? detail
            : "Couldn't save";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const removeSchedule = async () => {
    if (!scopeChildId) return;
    const childName = childList.find((c) => c.id === scopeChildId)?.name || "this child";
    if (!window.confirm(`Remove the parenting schedule for ${childName}? Day-by-day overrides for that child are kept.`)) return;
    try {
      await api.delete(`/custody/schedule?child_id=${encodeURIComponent(scopeChildId)}`);
      toast.success("Schedule removed");
      onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't remove"); }
  };

  // Download the next 90 days of handovers as a .ics calendar file.
  // Routes through the existing PdfDownload provider so on mobile the
  // OS share sheet pipes it straight into Apple/Google Calendar.
  const downloadHandoverFeed = () => {
    const today = new Date().toISOString().slice(0, 10);
    downloadFile(
      `${API}/custody/handovers.ics?days=90`,
      `coparent-handovers-${today.replace(/-/g, "")}.ics`,
      { kind: "ics", label: "Generating handover schedule" },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="parenting-schedule-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-heading">Parenting schedule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 text-sm">
            {/* v77.99.43 — "How this works" explainer. Distinguishes a
                recurring Parenting Schedule from a one-off Custody
                Change so users stop building ad-hoc Custody Events
                hoping they'll become a recurring arrangement.
                Collapsed by default; <summary> stays semantic. */}
            <details className="rounded-md border border-[#A7D8DA] bg-[#E0F2F2] text-[12px]" data-testid="schedule-how-it-works">
              <summary className="cursor-pointer px-3 py-2 font-medium text-[#0F5658] inline-flex items-center gap-2 select-none">
                How does this differ from a Custody event?
              </summary>
              <div className="px-3 pb-3 pt-1 text-[#0F5658] space-y-2 leading-relaxed">
                <div>
                  <span className="font-semibold">Parenting schedule</span> = your <span className="font-semibold">recurring</span> arrangement (e.g. alternating weeks, 2-2-3, every-second-weekend). Set it up once and the calendar fills in repeating custody blocks automatically.
                </div>
                <div>
                  <span className="font-semibold">Custody change (one-off)</span> = a single deviation from the recurring schedule — a swap, a holiday move, a temporary handover change. Use <span className="font-medium">New event → Custody change</span> for those.
                </div>
                <div className="text-[11px] text-[#116B6E]">
                  Common patterns: <span className="font-medium">Every second weekend</span>, <span className="font-medium">Alternating weeks</span>, <span className="font-medium">2-2-3</span>, <span className="font-medium">Week on / week off</span>.
                </div>
              </div>
            </details>
            {/* v77.99.51 — Top-level "No current parenting schedule"
                option. For households that genuinely don't share
                custody / access / handovers right now (sole-parent
                households, parallel parenting, etc.). Marking this
                flips the household-level `parenting_schedule_status`
                to `none_currently` and silences every "set up a
                schedule" nudge across the app. The schedule dialog
                then immediately closes; the user can revisit any
                time via the calm "Parenting schedule status" card
                that replaces the empty-state on the calendar. */}
            <button
              type="button"
              onClick={async () => {
                try {
                  await api.post("/custody/status", { status: "none_currently" });
                  toast.success(
                    "Noted — we won't prompt you to set up a schedule. You can revisit this from the Parenting schedule status card on Calendar if your arrangements change.",
                    { duration: 7000 },
                  );
                  onSaved?.();
                  onClose();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Couldn't save that — try again.");
                }
              }}
              className="w-full text-left rounded-md border border-[#E6CFA2] bg-[#FDF4E2] hover:bg-[#FBEFCC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C99A2A]/60 px-3 py-3 transition-colors"
              data-testid="schedule-dialog-none-currently-option"
            >
              <div className="font-medium text-[#5C3A1E] text-sm">No current parenting schedule</div>
              <p className="text-xs text-[#6F4E1E] mt-1 leading-relaxed">
                Choose this if there is currently no shared custody, access, or recurring handover arrangement in place. You can update this later if your parenting arrangements change.
              </p>
            </button>
            {/* v77.22.8 — Schedules are STRICTLY per-child. No
                "All children" option — the user must pick a specific
                child. Archived children are already excluded at the
                data source (include_archived=false). If the household
                has zero active children, the dialog surfaces a clear
                empty-state instead of an invalid form. */}
            {childList.length === 0 ? (
              <div className="rounded-md border border-[#E6CFA2] bg-[#FFF6E1] p-3 text-[12px] text-[#8F6B1F]" data-testid="schedule-no-children">
                You need at least one active child before you can configure a parenting schedule. Add a child under <span className="font-medium">Children</span> first.
              </div>
            ) : (
              <div data-testid="schedule-child-scope">
                <Label className="text-xs uppercase tracking-[0.08em] text-[#6F828A]">For which child?</Label>
                <Select value={scopeChildId || ""} onValueChange={(v) => setScopeChildId(v)}>
                  <SelectTrigger className="mt-1" data-testid="schedule-child-scope-select"><SelectValue placeholder="Pick a child" /></SelectTrigger>
                  <SelectContent>
                    {childList.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-[#6F828A] mt-1">
                  Each child has their own schedule. Only parents linked to this child can appear below.
                </div>
              </div>
            )}
            {/* v77.99.43 — Friendlier no-co-parent guidance. Offers a
                one-click CTA to add a placeholder co-parent from the
                Household settings page so a single / parallel-parenting
                user can still build a schedule even before the other
                parent joins (or if they never will). Also surfaces the
                "label shown on schedule" tip + the fact that placeholders
                can be promoted to real accounts later without losing
                history. */}
            {scopeChildId && eligibleParents.length < 2 && (
              <div className="rounded-md border border-[#E6CFA2] bg-[#FFF6E1] p-3 text-[12px] text-[#8F6B1F] space-y-2" data-testid="schedule-no-coparent">
                <div>
                  <span className="font-medium">No active co-parent found yet.</span> A parenting schedule needs two parents.
                </div>
                <div className="text-[11px] leading-relaxed">
                  You can add a <span className="font-medium">placeholder co-parent</span> now and invite them later. Placeholders can be promoted to a real account whenever they sign up — no history is lost.
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Link
                    to="/app/settings?section=household"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-[#C99A2A] bg-white text-[#6F4E1E] text-[11px] font-medium hover:bg-[#FBEFCC]"
                    data-testid="schedule-no-coparent-add-placeholder"
                  >
                    Add a placeholder co-parent
                  </Link>
                  <Link
                    to="/app/children"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-[#D9E1E3] bg-white text-[#2E3A3F] text-[11px] font-medium hover:bg-[#F4FAFA]"
                    data-testid="schedule-no-coparent-children"
                  >
                    Invite a real co-parent
                  </Link>
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-[0.08em] text-[#6F828A]">Pattern</Label>
              <Select value={pattern} onValueChange={setPattern}>
                <SelectTrigger className="mt-1" data-testid="schedule-pattern-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PATTERNS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.08em] text-[#6F828A]">Start date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1" required data-testid="schedule-start-date" />
              <div className="text-[11px] text-[#6F828A] mt-1">Day 0 of the cycle. Past dates are fine — used as the cycle anchor.</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.08em] text-[#6F828A]">Parent A</Label>
                <Select value={parentAId} onValueChange={setParentAId}>
                  <SelectTrigger className="mt-1" data-testid="schedule-parent-a"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{eligibleParents.map((m) => <SelectItem key={m.id} value={m.id}>{m.id === currentUserId ? `${m.name} (you)` : m.name}</SelectItem>)}</SelectContent>
                </Select>
                <div className="text-[10px] mt-1 inline-block px-1.5 py-0.5 rounded-sm border" style={{ backgroundColor: "#E6F1F8", borderColor: "#A7C5DA" }}>blue tint</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.08em] text-[#6F828A]">Parent B</Label>
                <Select value={parentBId} onValueChange={setParentBId}>
                  <SelectTrigger className="mt-1" data-testid="schedule-parent-b"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{otherMembers.map((m) => <SelectItem key={m.id} value={m.id}>{m.id === currentUserId ? `${m.name} (you)` : m.name}</SelectItem>)}</SelectContent>
                </Select>
                <div className="text-[10px] mt-1 inline-block px-1.5 py-0.5 rounded-sm border" style={{ backgroundColor: "#E7F3EA", borderColor: "#A9C7AC" }}>green tint</div>
              </div>
            </div>
            {pattern === "every_second_weekend" && (
              <div className="border border-[#D9E1E3] rounded-sm p-3 bg-[#F5F7F8] space-y-3" data-testid="schedule-weekend-window">
                <div className="text-xs uppercase tracking-[0.08em] text-[#6F828A]">Handover settings</div>
                <p className="text-[11px] text-[#6F828A] -mt-2">
                  When does the child move between homes? Set the start and end times for the weekend period. These times determine custody days and handover points on the calendar.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.08em] text-[#6F828A]">Weekend starts</Label>
                    <Select value={String(weekendStartDay)} onValueChange={(v) => setWeekendStartDay(parseInt(v, 10))}>
                      <SelectTrigger className="mt-1" data-testid="schedule-weekend-start-day"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {WEEKDAY_OPTIONS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input
                      type="time"
                      value={weekendStartTime}
                      onChange={(e) => setWeekendStartTime(e.target.value)}
                      className="mt-2"
                      required
                      data-testid="schedule-weekend-start-time"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.08em] text-[#6F828A]">Weekend ends</Label>
                    <Select value={String(weekendEndDay)} onValueChange={(v) => setWeekendEndDay(parseInt(v, 10))}>
                      <SelectTrigger className="mt-1" data-testid="schedule-weekend-end-day"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {WEEKDAY_OPTIONS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input
                      type="time"
                      value={weekendEndTime}
                      onChange={(e) => setWeekendEndTime(e.target.value)}
                      className="mt-2"
                      required
                      data-testid="schedule-weekend-end-time"
                    />
                  </div>
                </div>
                <div className="text-[11px] text-[#116B6E]" data-testid="schedule-weekend-preview">
                  Handover preview: <span className="font-medium">{weekendPreview}</span>
                </div>
                {existing?.pattern === "every_second_weekend" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={downloadHandoverFeed}
                    className="border-[#1F9FA4] text-[#116B6E] hover:bg-[#E0F2F2] rounded-sm w-full justify-start"
                    data-testid="schedule-download-handovers-btn"
                    title="Add the next 90 days of handovers to Apple, Google or Outlook calendar"
                  >
                    <CalendarPlus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.8} />
                    Add upcoming handovers to my calendar
                  </Button>
                )}
              </div>
            )}
            {pattern === "custom_weekly" && (
              <div>
                <Label className="text-xs uppercase tracking-[0.08em] text-[#6F828A]">Weekly assignment</Label>
                <div className="grid grid-cols-7 gap-1 mt-2" data-testid="schedule-custom-weekday-grid">
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((label, i) => {
                    const v = customWeekly[i] || "A";
                    return (
                      <button
                        type="button"
                        key={label}
                        onClick={() => {
                          const next = customWeekly.split("");
                          next[i] = v === "A" ? "B" : "A";
                          while (next.length < 7) next.push("A");
                          setCustomWeekly(next.join(""));
                        }}
                        className={`text-[11px] px-1 py-2 border rounded-sm`}
                        style={{ backgroundColor: v === "A" ? "#E6F1F8" : "#E7F3EA", borderColor: v === "A" ? "#A7C5DA" : "#A9C7AC" }}
                      >
                        <div className="font-medium text-[#2E3A3F]">{label}</div>
                        <div className="text-[9px] uppercase text-[#6F828A]">{v}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="text-[11px] text-[#6F828A] mt-1">Tap a day to flip its parent assignment.</div>
              </div>
            )}
            {/* v71 — Handover settings panel for ALL non-weekend patterns.
                Weekend pattern keeps its bespoke window editor above
                because the time pickers there ARE the handover times.
                Other patterns get an explicit transition list so the
                user pins each switchover time with plain language. */}
            {pattern !== "every_second_weekend" && transitions.length > 0 && parentAId && parentBId && (
              <div className="border border-[#D9E1E3] rounded-sm p-3 bg-[#F5F7F8] space-y-3" data-testid="schedule-handover-times">
                <div className="text-xs uppercase tracking-[0.08em] text-[#6F828A]">Handover settings</div>
                <p className="text-[11px] text-[#6F828A] -mt-2">
                  When does the child move between homes? Set a time for each handover. These times determine custody days and handover points on the calendar.
                </p>
                <div className="space-y-2">
                  {transitions.map((t, idx) => {
                    const fromName = parentLabel(t.from);
                    const toName = parentLabel(t.to);
                    const dayLabel = WEEKDAY_SHORT[t.day] || "";
                    return (
                      <div key={`${transitionKey(t)}-${idx}`} className="flex items-center gap-2 flex-wrap text-[12px]" data-testid={`schedule-handover-row-${idx}`}>
                        <span className="text-[#2E3A3F] flex-1 min-w-[180px]">
                          When does the child move from <span className="font-medium">{fromName}</span> to <span className="font-medium">{toName}</span>?
                        </span>
                        <span className="text-[11px] uppercase tracking-[0.08em] text-[#6F828A] bg-white border border-[#D9E1E3] rounded-sm px-1.5 py-0.5">
                          {dayLabel}
                        </span>
                        <Input
                          type="time"
                          value={transitionTime(t)}
                          onChange={(e) => setTransitionTime(t, e.target.value)}
                          className="w-[110px]"
                          required
                          data-testid={`schedule-handover-time-${idx}`}
                        />
                      </div>
                    );
                  })}
                </div>
                {schedulePreview && (
                  <div className="text-[11px] text-[#116B6E]" data-testid="schedule-handover-preview">
                    Handover preview: <span className="font-medium">{schedulePreview}</span>
                  </div>
                )}
                {existing && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={downloadHandoverFeed}
                    className="border-[#1F9FA4] text-[#116B6E] hover:bg-[#E0F2F2] rounded-sm w-full justify-start"
                    data-testid="schedule-download-handovers-btn-all"
                    title="Add the next 90 days of handovers to Apple, Google or Outlook calendar"
                  >
                    <CalendarPlus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.8} />
                    Add upcoming handovers to my calendar
                  </Button>
                )}
              </div>
            )}
          </div>
          {/* v77.26 — Co-parents see a message field + a banner explaining
              that their submission is a request the primary will review. */}
          {!isPrimaryParent && scopeChildId && eligibleParents.length >= 2 && (
            <div className="mt-3 space-y-2" data-testid="schedule-coparent-notice">
              <div className="rounded-md border border-[#A7D8DA] bg-[#E0F2F2] p-3 text-[12px] text-[#0F5658] flex items-start gap-2">
                <Inbox className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.6} />
                <span>
                  As a co-parent, your changes are sent as a <span className="font-medium">change request</span>.
                  The primary parent will review and approve before the schedule updates.
                </span>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.08em] text-[#6F828A]">
                  Message to the primary parent <span className="text-[#9AAAB1] normal-case tracking-normal">(optional)</span>
                </Label>
                <Textarea
                  value={proposalMessage}
                  onChange={(e) => setProposalMessage(e.target.value)}
                  placeholder="Why are you suggesting this change?"
                  className="mt-1 text-sm"
                  rows={2}
                  maxLength={1000}
                  data-testid="schedule-proposal-message"
                />
              </div>
            </div>
          )}
          <DialogFooter className="mt-4 flex flex-wrap gap-2">
            {existing && isPrimaryParent && (
              <Button type="button" variant="ghost" onClick={removeSchedule} className="text-[#8C2A22] hover:bg-[#FBEFED] mr-auto" data-testid="schedule-remove-btn">Remove schedule</Button>
            )}
            <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy || !scopeChildId || eligibleParents.length < 2} className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm disabled:opacity-60 disabled:cursor-not-allowed" data-testid="schedule-save-btn">
              {busy
                ? (isPrimaryParent ? "Saving…" : "Submitting…")
                : (isPrimaryParent ? "Save schedule" : "Request change")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RecurrenceFields({ form, setForm }) {
  const toggleDay = (d) => {
    const list = form.byweekday.includes(d) ? form.byweekday.filter((x) => x !== d) : [...form.byweekday, d].sort((a, b) => a - b);
    setForm({ ...form, byweekday: list });
  };
  const showWeekdays = form.freq === "weekly" || form.freq === "fortnightly";
  const mode = form.end_mode || "never";
  return (
    <div className="border border-[#D9E1E3] rounded-sm p-3 space-y-3">
      <Label className="inline-flex items-center gap-2 text-sm"><Repeat className="w-3.5 h-3.5" /> Repeat</Label>
      <Select value={form.freq} onValueChange={(v) => setForm({ ...form, freq: v })}>
        <SelectTrigger data-testid="event-freq-select"><SelectValue /></SelectTrigger>
        <SelectContent>{FREQ_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
      {showWeekdays && (
        <div>
          <div className="text-xs text-[#6F828A] mb-1.5">Which weekdays?</div>
          <div className="flex flex-wrap gap-1.5" data-testid="event-weekday-picker">
            {WEEKDAYS.map((d) => {
              const active = form.byweekday.includes(d.v);
              return (
                <button type="button" key={d.v} onClick={() => toggleDay(d.v)}
                  className={`px-2.5 py-1 border rounded-sm text-xs transition-colors ${active ? "bg-[#1F9FA4] border-[#1F9FA4] text-white" : "bg-white border-[#D9E1E3] text-[#6F828A] hover:bg-[#EEF3F4]"}`}
                  data-testid={`event-weekday-${d.v}`}
                >{d.l}</button>
              );
            })}
          </div>
          <div className="text-[11px] text-[#6F828A] mt-1.5">Leave all empty to use the start date&apos;s weekday.</div>
        </div>
      )}
      {form.freq && form.freq !== "none" && (
        <div>
          <div className="text-xs text-[#6F828A] mb-1.5">Ends</div>
          <div className="flex flex-col gap-1.5" data-testid="event-end-mode">
            {[
              { v: "never", label: "No end date" },
              { v: "on_date", label: "On date" },
              { v: "after_count", label: "After X occurrences" },
            ].map((opt) => {
              const active = mode === opt.v;
              return (
                <label
                  key={opt.v}
                  className={`flex items-center gap-2 px-2.5 py-2 border rounded-sm text-sm cursor-pointer select-none transition-colors min-h-[40px] ${
                    active
                      ? "bg-[#E8F0F1] border-[#1F9FA4] text-[#116B6E] font-medium"
                      : "bg-white border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#F5F7F8]"
                  }`}
                  data-testid={`event-end-mode-row-${opt.v}`}
                >
                  <input
                    type="radio"
                    name="end_mode"
                    value={opt.v}
                    checked={active}
                    onChange={() => setForm({ ...form, end_mode: opt.v })}
                    className="w-4 h-4 accent-[#1F9FA4] cursor-pointer"
                    data-testid={`event-end-mode-${opt.v}`}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
          {mode === "on_date" && (
            <Input type="date" value={form.until} onChange={(e) => setForm({ ...form, until: e.target.value })} className="mt-2" data-testid="event-until-input" />
          )}
          {mode === "after_count" && (
            <div className="flex items-center gap-2 mt-2">
              <Input type="number" min={1} max={365} value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} className="w-28" placeholder="e.g. 12" data-testid="event-count-input" />
              <span className="text-sm text-[#6F828A]">occurrences</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const REMINDER_PRESETS = [
  { v: 10, l: "10 minutes before" },
  { v: 120, l: "2 hours before" },
  { v: 720, l: "12 hours before" },
  { v: 1440, l: "24 hours before" },
];

export function ReminderFields({ form, setForm }) {
  const toggle = (v) => {
    const list = (form.reminders || []).includes(v) ? form.reminders.filter((x) => x !== v) : [...(form.reminders || []), v];
    setForm({ ...form, reminders: list });
  };
  return (
    <div className="border border-[#D9E1E3] rounded-sm p-3 space-y-3">
      <Label className="inline-flex items-center gap-2 text-sm">Reminders <span className="text-[#6F828A] text-xs font-normal">· visible only to you</span></Label>
      <div className="flex flex-wrap gap-1.5" data-testid="event-reminders-picker">
        {REMINDER_PRESETS.map((r) => {
          const active = (form.reminders || []).includes(r.v);
          return (
            <button type="button" key={r.v} onClick={() => toggle(r.v)}
              className={`px-2.5 py-1 border rounded-sm text-xs transition-colors ${active ? "bg-[#1F9FA4] border-[#1F9FA4] text-white" : "bg-white border-[#D9E1E3] text-[#6F828A] hover:bg-[#EEF3F4]"}`}
              data-testid={`event-reminder-${r.v}`}
            >{r.l}</button>
          );
        })}
      </div>
      <div>
        <Label className="text-xs text-[#6F828A]">Or custom — minutes before (optional)</Label>
        <Input type="number" min={1} max={10080} value={form.custom_minutes || ""} onChange={(e) => setForm({ ...form, custom_minutes: e.target.value })} placeholder="e.g. 45" className="mt-1" data-testid="event-reminder-custom-input" />
      </div>
    </div>
  );
}
